// -----------------------------------------------------------------------------
//  GPS capture for a phone standing in front of a house.
//
//  A single reading from a consumer handset is frequently 30-80 metres out,
//  especially in the first seconds after the receiver wakes up. It then
//  converges. So the app does not take "a reading" — it watches for a few
//  seconds and keeps the best sample. That is what "best-of-N" means, and it is
//  the difference between household pins that cluster correctly and household
//  pins that scatter across a rice field.
//
//  Deliberately NOT a hard gate. Gate 1 asked for an accuracy gate, and
//  migration 007 flags anything worse than 25 m — but the app still saves.
//  Refusing to save because the sky is cloudy teaches officers to fabricate
//  coordinates or to stop using the app, and both are worse than a flagged
//  record a supervisor can review.
// -----------------------------------------------------------------------------

export type Fix = {
  lat: number
  lon: number
  accuracy_m: number
  captured_at: string
  provider: string
  samples: number
}

export type FixQuality = 'good' | 'usable' | 'poor'

/** Matches the thresholds in quality_rules, so the app and the database agree. */
export function fixQuality(accuracy_m: number): FixQuality {
  if (accuracy_m <= 10) return 'good'
  if (accuracy_m <= 25) return 'usable'
  return 'poor'
}

export function qualityMessage(accuracy_m: number): string {
  const q = fixQuality(accuracy_m)
  if (q === 'good') return `Good location (±${Math.round(accuracy_m)} m).`
  if (q === 'usable') return `Acceptable location (±${Math.round(accuracy_m)} m).`
  return (
    `Weak location (±${Math.round(accuracy_m)} m). ` +
    `Step into the open, away from walls and trees, and try again. ` +
    `You can still save — it will be marked for a supervisor to check.`
  )
}

export type CaptureProgress = { seconds: number; best: number | null; samples: number }

/**
 * Watch the GPS for up to `maxSeconds`, keeping the most accurate sample.
 * Stops early once `targetAccuracy` is reached, so a good fix does not make
 * the officer wait.
 */
export function captureFix(opts?: {
  maxSeconds?: number
  targetAccuracy?: number
  onProgress?: (p: CaptureProgress) => void
}): { promise: Promise<Fix>; cancel: () => void } {
  const maxSeconds = opts?.maxSeconds ?? 12
  const target = opts?.targetAccuracy ?? 8

  let watchId: number | null = null
  let ticker: ReturnType<typeof setInterval> | null = null
  let best: GeolocationPosition | null = null
  let samples = 0
  let settled = false

  const cleanup = () => {
    if (watchId !== null && navigator.geolocation) navigator.geolocation.clearWatch(watchId)
    if (ticker) clearInterval(ticker)
    watchId = null
    ticker = null
  }

  const promise = new Promise<Fix>((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('This phone or browser cannot provide a location.'))
      return
    }

    const finish = () => {
      if (settled) return
      settled = true
      cleanup()
      if (!best) {
        reject(
          new Error(
            'No location yet. Check that location is switched on for this phone and allowed for this site, then try again.',
          ),
        )
        return
      }
      resolve({
        lat: best.coords.latitude,
        lon: best.coords.longitude,
        accuracy_m: Math.round(best.coords.accuracy * 10) / 10,
        captured_at: new Date(best.timestamp).toISOString(),
        // Distinguishing true GPS from a network-derived guess matters: a
        // 2000 m "fix" from a mobile mast is not a household location.
        provider: best.coords.accuracy <= 100 ? 'gps' : 'network',
        samples,
      })
    }

    let elapsed = 0
    ticker = setInterval(() => {
      elapsed += 1
      opts?.onProgress?.({
        seconds: elapsed,
        best: best ? Math.round(best.coords.accuracy) : null,
        samples,
      })
      if (elapsed >= maxSeconds) finish()
    }, 1000)

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        samples += 1
        if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos
        if (pos.coords.accuracy <= target) finish()
      },
      (err) => {
        if (settled) return
        settled = true
        cleanup()
        // The browser's own messages are unhelpful ("User denied Geolocation").
        const message =
          err.code === err.PERMISSION_DENIED
            ? 'Location permission was refused. Allow location for this site in the browser settings, then try again.'
            : err.code === err.POSITION_UNAVAILABLE
              ? 'The phone could not find a location. Step outside, away from buildings, and try again.'
              : 'Finding the location took too long. Try again in the open.'
        reject(new Error(message))
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: maxSeconds * 1000 },
    )
  })

  return {
    promise,
    cancel: () => {
      settled = true
      cleanup()
    },
  }
}

/**
 * A stable identifier for this phone, so a record can be traced back to the
 * device that produced it. Random, stored locally, and containing nothing
 * about the person — it is for debugging sync, not for tracking anyone.
 */
export function deviceId(): string {
  const KEY = 'vm.device_id'
  let id = localStorage.getItem(KEY)
  if (!id) {
    id = 'dev-' + crypto.randomUUID().slice(0, 8)
    localStorage.setItem(KEY, id)
  }
  return id
}
